import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faCheck, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { updateNodeValue } from "../../../../../services/method";
import {  Main, Styled } from "./style";
import CopyToClipboard from "react-copy-to-clipboard";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { webURL } from "../../../../../services/API";

type MetaListenerTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function MetaListenerTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: MetaListenerTriggerProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [webhookUrl, setWebhookUrl] = React.useState<string>("");
  const [isCopy, setIsCopy] = React.useState<boolean>(false);
  const [isCopyToken, setIsCopyToken] = React.useState<boolean>(false);
    React.useState<boolean>(false);
  const [verifyToken, setVerifyToken] = React.useState<string>("");
  const [expandedStep, setExpandedStep] = React.useState<number | null>(null);

  const toggleStep = (stepNumber: number) => {
    setExpandedStep((prev) => (prev === stepNumber ? null : stepNumber));
  };

  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  // Initialize webhook URL, verify token, and check watch status
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get("id");

    const url = `${webURL}/triggers/meta/webhook?projectId=${projectId}&nodeId=${id}&token=${verifyToken}`;
    console.log("Meta Webhook URL:", url);
    setWebhookUrl(url);

    // Generate or retrieve verify token
    if (!data.value?.verifyToken) {
      const randomToken = `verify_${Math.random()
        .toString(36)
        .substring(2, 15)}_${Date.now()}`;
      setVerifyToken(randomToken);
      updateNodeValue(id, setNodes, { verifyToken: randomToken });
    } else {
      setVerifyToken(data.value.verifyToken);
    }

  }, [
    data.value?.isWatching,
    data.value?.enableMessenger,
    data.value?.enableInstagram,
    data.value?.verifyToken,
    id,
    setNodes,
    verifyToken,
  ]);

  const isWarning = false;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={ 21 } /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
         </> : null}
        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={false}
            style={{ minWidth: "450px" }}>
            {/* {isLoading ? (
              <Loader />
            ) : ( */}
              <Styled>

                {/* Webhook URL Section */}
                <div className="webhook-section">
                  <h4>Webhook URL</h4>
                  <CopyToClipboard
                    text={webhookUrl}
                    onCopy={() => {
                      setIsCopy(true);
                      setTimeout(() => setIsCopy(false), 2000);
                    }}>
                    <div className="link-container" title={webhookUrl}>
                      <div className="link-with-copy">
                        <FontAwesomeIcon
                          icon={isCopy ? faCheck : faClipboard}
                          className={`copy-icon ${isCopy ? "copied" : ""}`}
                        />
                        <span className="link-text">{webhookUrl}</span>
                      </div>
                    </div>
                  </CopyToClipboard>
                </div>
                <div className="webhook-section">
                  <h4>Verify Token</h4>
                  <CopyToClipboard
                    text={verifyToken}
                    onCopy={() => {
                      setIsCopyToken(true);
                      setTimeout(() => setIsCopyToken(false), 2000);
                    }}>
                    <div className="link-container" title={verifyToken}>
                      <div className="link-with-copy">
                        <FontAwesomeIcon
                          icon={isCopyToken ? faCheck : faClipboard}
                          className={`copy-icon ${isCopyToken ? "copied" : ""}`}
                        />
                        <span className="link-text">{verifyToken}</span>
                      </div>
                    </div>
                  </CopyToClipboard>
                </div>

                {/* Setup Instructions - Accordion */}
                <div className="instructions-section">
                  <h5>Setup Instructions:</h5>
                  <div className="accordion">
                    {/* Step 1 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(1)}>
                        <span className="step-number">1.</span>
                        <span className="step-title">Create Facebook App</span>
                        <span
                          className={`arrow ${
                            expandedStep === 1 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 1 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              Go to{" "}
                              <a
                                href="https://developers.facebook.com/apps"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="external-link">
                                Facebook App Dashboard
                                <FontAwesomeIcon
                                  icon={faExternalLinkAlt}
                                  size="xs"
                                />
                              </a>
                            </li>
                            <li>
                              Click <strong>Create App</strong>
                            </li>
                            <li>
                              Choose <strong>Other</strong> →{" "}
                              <strong>Other</strong> for use case
                            </li>
                            <li>
                              Select app type: <strong>Business</strong>
                            </li>
                            <li>
                              Choose your <strong>Business Portfolio</strong>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 2 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(2)}>
                        <span className="step-number">2.</span>
                        <span className="step-title">
                          Configure App Settings
                        </span>
                        <span
                          className={`arrow ${
                            expandedStep === 2 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 2 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              Go to <strong>App Settings</strong> →{" "}
                              <strong>Basic</strong>
                            </li>
                            <li>
                              Add <strong>Privacy Policy URL</strong> (use your
                              website or{" "}
                              <a
                                href="https://www.notion.so/This-Privacy-Policy-2b70c2f6552780569028ff49b5d5e3af?source=copy_link"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="external-link">
                                this template
                                <FontAwesomeIcon
                                  icon={faExternalLinkAlt}
                                  size="xs"
                                />
                              </a>
                              )
                            </li>
                            <li>Save changes</li>
                            <li>
                              In header, toggle to{" "}
                              <strong>Make app Live</strong>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 3 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(3)}>
                        <span className="step-number">3.</span>
                        <span className="step-title">Set Up Messenger</span>
                        <span
                          className={`arrow ${
                            expandedStep === 3 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 3 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              Navigate to <strong>Messenger</strong> →{" "}
                              <strong>Settings</strong>
                            </li>
                            <li>
                              In <strong>Webhooks</strong> section, click{" "}
                              <strong>Add Callback URL</strong>
                            </li>
                            <li>
                              Paste the <strong>Webhook URL</strong> from above
                            </li>
                            <li>
                              Paste the <strong>Verify Token</strong> from above
                            </li>
                            <li>
                              Click <strong>Verify and Save</strong>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 4 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(4)}>
                        <span className="step-number">4.</span>
                        <span className="step-title">
                          Generate Access Token
                        </span>
                        <span
                          className={`arrow ${
                            expandedStep === 4 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 4 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              In <strong>Access Tokens</strong> section, click{" "}
                              <strong>Connect Page</strong>
                            </li>
                            <li>Select your Facebook Page</li>
                            <li>
                              Copy the generated{" "}
                              <strong>Page Access Token</strong>
                            </li>
                            <li>
                              Save this token in your Meta connection settings
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 5 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(5)}>
                        <span className="step-number">5.</span>
                        <span className="step-title">
                          Subscribe to Webhook Events
                        </span>
                        <span
                          className={`arrow ${
                            expandedStep === 5 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 5 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              In Webhooks section, find your page subscription
                            </li>
                            <li>
                              Click <strong>Edit</strong>
                            </li>
                            <li>
                              Subscribe to: <code>messages</code>,{" "}
                              <code>message_reads</code>, <code>feed</code>
                            </li>
                            <li>
                              Click <strong>Save</strong>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 6 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(6)}>
                        <span className="step-number">6.</span>
                        <span className="step-title">Request Permissions</span>
                        <span
                          className={`arrow ${
                            expandedStep === 6 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 6 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              Go to <strong>App Review</strong> →{" "}
                              <strong>Permissions and Features</strong>
                            </li>
                            <li>
                              Request: <code>pages_messaging</code>,{" "}
                              <code>pages_read_engagement</code>
                            </li>
                            <li>
                              Wait for approval (usually instant for
                              development)
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Step 7 */}
                    <div className="accordion-item">
                      <div
                        className="accordion-header"
                        onClick={() => toggleStep(7)}>
                        <span className="step-number">7.</span>
                        <span className="step-title">Test the Integration</span>
                        <span
                          className={`arrow ${
                            expandedStep === 7 ? "expanded" : ""
                          }`}>
                          ▼
                        </span>
                      </div>
                      {expandedStep === 7 && (
                        <div className="accordion-content">
                          <ul>
                            <li>
                              Click <strong>Start Watching</strong> button above
                            </li>
                            <li>
                              Send a message to your Facebook Page via Messenger
                            </li>
                            <li>Verify the webhook receives the message</li>
                            <li>Check the workflow logs for incoming events</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Watch Info */}
                {data.value?.isWatching && (
                  <div className="watch-info">
                    <ul>
                      <li>
                        <strong>Status:</strong>{" "}
                        <span className="status-enabled">Active</span>
                      </li>
                      <li>
                        <strong>Messenger:</strong>{" "}
                        {data.value?.enableMessenger
                          ? "✅ Enabled"
                          : "❌ Disabled"}
                      </li>
                      <li>
                        <strong>Instagram:</strong>{" "}
                        {data.value?.enableInstagram
                          ? "✅ Enabled"
                          : "❌ Disabled"}
                      </li>
                      {data.value?.watchStartedAt && (
                        <li>
                          <strong>Started:</strong>{" "}
                          {new Date(data.value.watchStartedAt).toLocaleString()}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </Styled>
            {/* )} */}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}